import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { Button, Popover, Space, Table, TableColumnsType } from "antd";
import { ResponseEntryDetail } from "api/generated";
import moment from "moment";
import { Link, RouteComponentProps } from "react-router-dom";
import { UsePaginationTableReturn } from "hooks/commons/usePaginationTable";
import { useContext, useMemo } from "react";
import { UseTableSelectionReturn } from "hooks/commons/useTableSelection";
import { COMPLEMENT_PARENT } from "routes/cfdiComplements/ComplementsScreen";
import DistributionPointSelected from "context/MyDistributionPoint";
import useLoggedUser from "hooks/useLoggedUser";

interface TraspasosTableProps extends RouteComponentProps {
  selection: UseTableSelectionReturn;
  isLoading: boolean;
  reloadTable: () => void;
  pagination: UsePaginationTableReturn<ResponseEntryDetail>;
  deleteComplement: (record: ResponseEntryDetail) => void;
  editTraspaso: (record: ResponseEntryDetail) => void;
}

function TraspasosTable({
  selection,
  isLoading,
  pagination,
  deleteComplement,
  editTraspaso,
  location,
}: TraspasosTableProps) {
  const traspasos = useMemo(() => pagination.items, [pagination.items]);
  const { distributionPoint } = useContext(DistributionPointSelected);
  const { providerDataOrigin } = useLoggedUser();

  const columns: TableColumnsType<ResponseEntryDetail> = [
    {
      title: "Número de registro",
      dataIndex: "inputRegisterFolio",
    },
    {
      title: "Volumen Inicial del Tanque",
      dataIndex: "initialVolumeTank",
      render: (initialVolumeTank: number) =>
        Intl.NumberFormat("en", { notation: "standard" }).format(
          initialVolumeTank,
        ),
    },
    {
      title: "Volumen Final del Tanque",
      dataIndex: "finalVolumeTank",
      render: (finalVolumeTank: number) =>
        Intl.NumberFormat("en", { notation: "standard" }).format(
          finalVolumeTank,
        ),
    },
    {
      title: "Volumen Recepción",
      dataIndex: "inputRegisterVolume",
      render: (inputRegisterVolume: number) =>
        Intl.NumberFormat("en", { notation: "standard" }).format(
          inputRegisterVolume,
        ),
    },
    {
      title: "Temperatura",
      dataIndex: "temperature",
      render: (temperature: number) =>
        Intl.NumberFormat("en", { notation: "standard" }).format(temperature),
    },
    {
      title: "Presión Absoluta",
      dataIndex: "absolutePressure",
      render: (absolutePressure: number) =>
        Intl.NumberFormat("en", { notation: "standard" }).format(
          absolutePressure,
        ),
    },
    {
      title: "Fecha de inicio",
      dataIndex: "inputRegisterReceptionDate",
      render: (date: string) => moment(date).format("YYYY/MM/DD HH:mm:ss"),
    },
    {
      title: "Fecha de fin",
      dataIndex: "inputRegisterEndReceptionDate",
      render: (date: string) => moment(date).format("YYYY/MM/DD HH:mm:ss"),
      sorter: true,
    },

    { title: "Almacén", dataIndex: "inputRegisterWarehouseKey" },
    {
      title: "Edición",
      dataIndex: "action",
      render: (_: unknown, record: ResponseEntryDetail) => (
        <div>
          <span>
            <Button
              icon={<EditOutlined />}
              title="Editar Traspaso"
              onClick={() => editTraspaso(record)}
            />
          </span>
        </div>
      ),
    },
    {
      title: "Complemento",
      dataIndex: "complement",
      render: (_: unknown, record: ResponseEntryDetail) => {
        const isEditable = record.isEditAllowed;
        const hasComplements = record.complement;

        if (!isEditable && !hasComplements) {
          return <span>Sin complemento asociado</span>;
        }
        const complementState = isEditable
          ? hasComplements
            ? "edit"
            : "add"
          : "view";

        const complementConfig = {
          add: {
            label: "Agregar información para complemento",
            icon: <PlusOutlined />,
          },
          edit: {
            label: "Editar información de complemento",
            icon: <EditOutlined />,
          },
          view: {
            label: "Ver información de complemento",
            icon: <EyeOutlined />,
          },
        };
        const currentComplement = complementConfig[complementState];

        return (
          <Space>
            <Link
              key="toComplement"
              to={{
                pathname: `/complement/${record.id}/${COMPLEMENT_PARENT.TRASPASO}/${distributionPoint?.id}`,
                state: {
                  from: location.pathname + location.search,
                },
              }}
            >
              <Popover content={currentComplement.label}>
                <Button icon={currentComplement.icon} />
              </Popover>
            </Link>
            {hasComplements && (
              <Popover content="Eliminar información de complemento">
                <Button
                  icon={<DeleteOutlined />}
                  danger
                  onClick={() => {
                    deleteComplement(record);
                  }}
                />
              </Popover>
            )}
          </Space>
        );
      },
    },
  ].filter((column) => {
    if (providerDataOrigin === "EXTERNAL" && column.title === "Edición") {
      return false;
    }
    return true;
  });

  return (
    <Table
      rowSelection={selection.rowSelection}
      rowKey="id"
      style={{ width: "100%" }}
      columns={columns}
      dataSource={traspasos}
      loading={isLoading}
      pagination={{
        ...pagination.paginationConfig,
        onChange: pagination.onPageChange,
        onShowSizeChange: pagination.onPageSizeChange,
      }}
      scroll={{
        x: 1500,
        y: "calc(100vh - 600px)",
      }}
    />
  );
}

export default TraspasosTable;
